import { useParams, useNavigate, Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { HostingsAPI } from '../api';
import { Check, ChevronLeft, ChevronRight, X, Home, ChevronDown } from 'lucide-react';
import { SafeHtml } from './SafeHtml';

const getBandwidthPercent = (bandwidth: string | null) => {
    if (!bandwidth) return 100
    const bw = bandwidth.toLowerCase()
    if (bw.includes('ilimitado') || bw.includes('sin límite') || bw.includes('sin limite') || bw.includes('sin límites')) return 100

    const match = bw.match(/([\d,.]+)\s*(tb|gb|mb)?/)
    if (!match) return 0

    let value = parseFloat(match[1].replace(',', '.'))
    const unit = match[2]

    if (unit === 'tb') value *= 1000
    else if (unit === 'mb') value /= 1000

    // 1500GB is 100%
    return Math.min((value / 1500) * 100, 100)
}

const HostingDetail = () => {
    const { name } = useParams<{ name: string }>();
    const navigate = useNavigate();
    const [hosting, setHosting] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [relatedHostings, setRelatedHostings] = useState<any[]>([]);
    const [prevPlan, setPrevPlan] = useState<any>(null);
    const [nextPlan, setNextPlan] = useState<any>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const handleNextImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!hosting || !hosting.photos || !selectedImage) return;

        const currentIndex = hosting.photos.indexOf(selectedImage);
        if (currentIndex === -1) return;

        const nextIndex = (currentIndex + 1) % hosting.photos.length;
        setSelectedImage(hosting.photos[nextIndex]);
    };

    const handlePrevImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!hosting || !hosting.photos || !selectedImage) return;

        const currentIndex = hosting.photos.indexOf(selectedImage);
        if (currentIndex === -1) return;

        const prevIndex = (currentIndex - 1 + hosting.photos.length) % hosting.photos.length;
        setSelectedImage(hosting.photos[prevIndex]);
    };

    useEffect(() => {
        const checkScroll = () => {
            if (scrollContainerRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
                setCanScrollLeft(scrollLeft > 0);
                setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
            }
        };

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            // Add a small delay to ensure rendering is complete before checking scroll
            setTimeout(checkScroll, 100);
            window.addEventListener('resize', checkScroll);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', checkScroll);
            }
            window.removeEventListener('resize', checkScroll);
        };
    }, [relatedHostings]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const scrollAmount = direction === 'left' ? -300 : 300;
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const fetchHosting = async () => {
            if (!name) return;

            try {
                setLoading(true);
                setError(null);

                // Decode the URL parameter
                const decodedName = decodeURIComponent(name);

                // Fetch all hostings and find the one with matching name
                const res = await HostingsAPI.list();
                if (res.success && res.data) {
                    const foundHosting = res.data.find((h: any) => h.name === decodedName);
                    if (foundHosting) {
                        setHosting(foundHosting);

                        // Find prev/next hosting in the same category (payType)
                        const categoryHostings = res.data
                            .filter((h: any) => h.payType === foundHosting.payType && h.isVisible !== false)
                            .sort((a: any, b: any) => (a.price || 0) - (b.price || 0));

                        const currentIndex = categoryHostings.findIndex((h: any) => h.id === foundHosting.id);
                        
                        if (currentIndex !== -1) {
                            const prev = categoryHostings[currentIndex - 1] || categoryHostings[categoryHostings.length - 1];
                            const next = categoryHostings[currentIndex + 1] || categoryHostings[0];
                            setPrevPlan(prev.id !== foundHosting.id ? prev : null);
                            setNextPlan(next.id !== foundHosting.id ? next : null);
                        }

                        // Find related hostings (same payType, random selection)
                        const others = res.data.filter((h: any) =>
                            h.id !== foundHosting.id &&
                            h.payType === foundHosting.payType
                        );

                        // Shuffle and take 4
                        const shuffled = others.sort(() => 0.5 - Math.random());
                        setRelatedHostings(shuffled.slice(0, 4));
                    } else {
                        setError('Hosting no encontrado');
                    }
                } else {
                    setError('Error al cargar los datos');
                }
            } catch (err) {
                console.error('Error fetching hosting:', err);
                setError('Error al cargar los datos');
            } finally {
                setLoading(false);
            }
        };

        fetchHosting();
    }, [name]);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">Cargando...</p>
                </div>
            </div>
        );
    }

    if (error || !hosting) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <button
                    onClick={() => navigate(-1)}
                    className="mb-8 flex items-center gap-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                >
                    <ChevronLeft size={20} />
                    Volver
                </button>
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-8 text-center">
                    <p className="text-red-600 dark:text-red-400 text-lg">{error || 'Hosting no encontrado'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 relative overflow-x-hidden">
            {/* Fixed Navigation Arrows */}
            {prevPlan && (
                <div
                    className="fixed left-0 top-1/2 -translate-y-1/2 z-40 group cursor-pointer hidden lg:block"
                    onClick={() => {
                        navigate(`/hosting/${encodeURIComponent(prevPlan.name)}`);
                        window.scrollTo(0, 0);
                    }}
                >
                    <div className="relative flex items-center">
                        {/* Hover Card (Expands from left) */}
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 shadow-xl border border-gray-100 dark:border-slate-700 rounded-r-xl h-24 w-12 group-hover:w-72 transition-all duration-300 overflow-hidden flex items-center z-10">
                            {/* Arrow Container (Always visible part) */}
                            <div className="absolute left-0 w-12 h-full flex items-center justify-center bg-gray-100 dark:bg-slate-700/50 group-hover:bg-white dark:group-hover:bg-slate-800 transition-colors z-20">
                                <ChevronLeft size={24} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                            </div>

                            {/* Content (Hidden until hover) */}
                            <div className="pl-16 pr-4 flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-full min-w-[280px]">
                                {prevPlan.mainPhotoUrl ? (
                                    <img src={prevPlan.mainPhotoUrl} alt="" className="w-12 h-12 object-contain" />
                                ) : (
                                    <div className="w-12 h-12 bg-blue-50 dark:bg-slate-700 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold">
                                        {prevPlan.displayName.substring(0, 2)}
                                    </div>
                                )}
                                <div className="flex flex-col min-w-0 flex-1">
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Anterior</span>
                                    <span className="font-bold text-sm text-gray-900 dark:text-white truncate block">{prevPlan.displayName}</span>
                                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{prevPlan.price} {prevPlan.currency}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {nextPlan && (
                <div
                    className="fixed right-0 top-1/2 -translate-y-1/2 z-40 group cursor-pointer hidden lg:block"
                    onClick={() => {
                        navigate(`/hosting/${encodeURIComponent(nextPlan.name)}`);
                        window.scrollTo(0, 0);
                    }}
                >
                    <div className="relative flex items-center justify-end">
                        {/* Hover Card (Expands from right) */}
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 shadow-xl border border-gray-100 dark:border-slate-700 rounded-l-xl h-24 w-12 group-hover:w-72 transition-all duration-300 overflow-hidden flex items-center z-10 justify-end">
                            
                            {/* Content (Hidden until hover) */}
                            <div className="pr-16 pl-4 flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-full min-w-[280px] justify-end text-right">
                                <div className="flex flex-col min-w-0 flex-1">
                                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Siguiente</span>
                                    <span className="font-bold text-sm text-gray-900 dark:text-white truncate block">{nextPlan.displayName}</span>
                                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{nextPlan.price} {nextPlan.currency}</span>
                                </div>
                                {nextPlan.mainPhotoUrl ? (
                                    <img src={nextPlan.mainPhotoUrl} alt="" className="w-12 h-12 object-contain" />
                                ) : (
                                    <div className="w-12 h-12 bg-blue-50 dark:bg-slate-700 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold">
                                        {nextPlan.displayName.substring(0, 2)}
                                    </div>
                                )}
                            </div>

                            {/* Arrow Container (Always visible part) */}
                            <div className="absolute right-0 w-12 h-full flex items-center justify-center bg-gray-100 dark:bg-slate-700/50 group-hover:bg-white dark:group-hover:bg-slate-800 transition-colors z-20">
                                <ChevronRight size={24} className="text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
                <nav className="flex mt-8" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-3">
                        <li className="inline-flex items-center">
                            <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white">
                                <Home className="w-4 h-4 mr-2" />
                                Inicio
                            </Link>
                        </li>
                        <li>
                            <div className="flex items-center">
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                <Link to="/hostings" className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white">Hosting</Link>
                            </div>
                        </li>
                        <li aria-current="page">
                            <div className="flex items-center">
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">{hosting.displayName}</span>
                            </div>
                        </li>
                    </ol>
                </nav>

                {/* Hero Section with Video */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                    {/* Left Content */}
                    <div className="space-y-8">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm font-medium">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                                    </svg>
                                    Contratar despliegue de infraestructura
                                </span>
                            </div>

                            <div className="flex items-center gap-4">
                                {hosting.mainPhotoUrl && (
                                    <img
                                        src={hosting.mainPhotoUrl}
                                        alt={hosting.displayName}
                                        className="w-16 h-16 object-contain"
                                    />
                                )}
                                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
                                    {hosting.displayName}
                                </h1>
                            </div>
                            <p className="mt-6 text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                                Potencia tu proyecto con nuestra infraestructura.
                            </p>
                        </div>

                        <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-bold text-blue-600 dark:text-blue-400">{hosting.currency}</span>
                            <span className="text-6xl font-bold text-gray-900 dark:text-white">{hosting.price}</span>
                            <span className="text-2xl text-gray-500 dark:text-gray-400">
                                {hosting.payTypeDisplay || (hosting.payType === 'ANUAL' ? 'por año' : 'por mes')}
                            </span>
                        </div>



                        <button
                            onClick={() => document.getElementById('tech-specs')?.scrollIntoView({ behavior: 'smooth' })}
                            className="text-blue-600 dark:text-blue-400 font-medium hover:underline flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Ver detalles de despliegue de infraestructura
                        </button>

                        <div className="pt-2">
                            <button className="px-10 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5">
                                Elegir plan
                            </button>
                        </div>
                    </div>

                    {/* Right Visual - Looped Video (Light Mode) / Image (Dark Mode) */}
                    <div className="relative flex justify-center lg:justify-end">
                        <video
                            src="/assets/video/decoracionTecnologia1.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="relative w-full max-w-lg object-contain dark:hidden"
                            style={{ maxHeight: '500px' }}
                        />
                        <img
                            src="/assets/fotoDarkVidieo.jpg"
                            alt="Decoración tecnología"
                            className="relative w-full max-w-lg object-contain hidden dark:block"
                            style={{ maxHeight: '500px' }}
                        />
                    </div>
                </div>

                {/* Main Details Grid */}
                <div id="tech-specs" className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 scroll-mt-32">
                    {/* Technical Specs */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
                        <div className="bg-gray-50 dark:bg-slate-700/50 p-6 border-b border-gray-200 dark:border-slate-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Especificaciones Técnicas
                            </h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <DetailRow label="Recursos" value={hosting.resources === 'COMPARTIDOS' ? 'Compartidos' : 'Dedicados'} />
                            <DetailRow label="Sitios máximos" value={hosting.sites === 'ONE' ? '1' : hosting.sites === 'TWO' ? '2' : 'Múltiples'} />
                            <DetailRow label="Sistema" value={hosting.hostingSystem === 'APACHE' ? 'Apache' : hosting.hostingSystem === 'NGINX' ? 'Nginx' : 'Otro'} />
                            {/* Space Bar */}
                            <div className="py-2">
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="font-bold text-gray-900 dark:text-gray-100">Espacio: {hosting.spaceGb} GB</span>
                                    <span className="text-gray-400 text-xs">de 50 GB</span>
                                </div>
                                <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5">
                                    <div
                                        className={`h-1.5 rounded-full ${(hosting.spaceGb / 50) * 100 < 25 ? 'bg-red-500' :
                                            (hosting.spaceGb / 50) * 100 < 50 ? 'bg-orange-500' :
                                                (hosting.spaceGb / 50) * 100 < 80 ? 'bg-green-500' : 'bg-green-600'
                                            }`}
                                        style={{ width: `${Math.min((hosting.spaceGb / 50) * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Bandwidth Bar */}
                            <div className="py-2">
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="font-bold text-gray-900 dark:text-gray-100">Bandwidth: {hosting.bandwidth || 'Ilimitado'}</span>
                                    <span className="text-gray-400 text-xs">de Max.</span>
                                </div>
                                <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5">
                                    {(() => {
                                        const percent = getBandwidthPercent(hosting.bandwidth)
                                        return (
                                            <div
                                                className={`h-1.5 rounded-full ${percent < 25 ? 'bg-red-500' :
                                                    percent < 50 ? 'bg-orange-500' :
                                                        percent < 80 ? 'bg-green-500' : 'bg-green-600'
                                                    }`}
                                                style={{ width: `${percent}%` }}
                                            ></div>
                                        )
                                    })()}
                                </div>
                            </div>
                            <DetailRow label="Cuentas de email" value={hosting.emails === 0 ? 'Sin cuentas de email' : `${hosting.emails} cuentas`} />
                            <DetailRow
                                label="Datacenter seleccionable"
                                value={hosting.dataCenterSelectable ? 'Sí' : 'No'}
                                highlight={hosting.dataCenterSelectable}
                            />
                            <DetailRow
                                label="SSL gratis"
                                value={hosting.ssl ? 'Incluido' : 'No incluido'}
                                icon={hosting.ssl ? <Check className="w-5 h-5 text-green-500" /> : null}
                            />
                        </div>
                    </div>

                    {/* Features */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
                        <div className="bg-gray-50 dark:bg-slate-700/50 p-6 border-b border-gray-200 dark:border-slate-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Características
                            </h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <DetailRow
                                label="Respaldos"
                                value={hosting.backups ? 'Sí' : 'No'}
                                highlight={hosting.backups}
                            />
                            {hosting.backups && hosting.backupsType && (
                                <DetailRow label="Tipo de respaldos" value={hosting.backupsType} />
                            )}
                            {/* Security Bar */}
                            <div className="py-2 border-b border-gray-100 dark:border-slate-700">
                                <span className="font-bold text-sm text-gray-900 dark:text-gray-100">Seguridad {hosting.security === 'NONE' ? 'No' : hosting.security === 'BASIC' ? 'Básica' : hosting.security === 'MEDIUM' ? 'Media' : 'Avanzada'}</span>
                                <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5 mt-2">
                                    <div
                                        className={`h-1.5 rounded-full ${hosting.security === 'NONE' ? 'bg-red-500 w-[10%]' :
                                            hosting.security === 'BASIC' ? 'bg-orange-500 w-[30%]' :
                                                hosting.security === 'MEDIUM' ? 'bg-green-500 w-[60%]' : 'bg-green-600 w-[100%]'
                                            }`}
                                    ></div>
                                </div>
                            </div>
                            <DetailRow
                                label="Caché"
                                value={
                                    hosting.cache === 'NONE' ? 'No' :
                                        hosting.cache === 'SI' ? 'Sí' :
                                            hosting.cache === 'DINAMICA' ? 'Dinámica' :
                                                hosting.cache === 'OBJETOS' ? 'De objetos' : 'Dinámica y Objetos'
                                }
                            />
                            <DetailRow
                                label="Optimización de imágenes"
                                value={
                                    hosting.imageOptimization === 'NONE' ? 'No' :
                                        hosting.imageOptimization === 'SI' ? 'Sí' : 'Smush Pro'
                                }
                            />
                        </div>
                    </div>
                </div>

            </div> {/* Close main container for full width section */}

            {/* Full Width Detailed Info Section */}
            {(hosting.resourcesDetail || hosting.dataCenterDetail || hosting.backupsDetail || hosting.securityDetail) && (
                <div
                    className="w-full relative py-20 my-16 border-y border-gray-200 dark:border-slate-700 bg-[#F1F4F9] dark:bg-slate-900 overflow-hidden"
                >
                    {/* Background Image with Parallax feel */}
                    <div
                        className="absolute inset-0 z-0 bg-auto bg-right-top bg-no-repeat dark:hidden pointer-events-none"
                        style={{ backgroundImage: "url('/assets/backgroundDetalles.jpg')" }}
                    ></div>

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="p-8 md:p-12">
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-10 text-center">
                                Información Detallada del Servicio
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16">
                                {/* Top Row: 3 Columns */}
                                {hosting.securityDetail && (
                                    <div className="space-y-10">
                                        <div className="group">
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                                                <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                                                </div>
                                                Seguridad
                                            </h3>
                                            <SafeHtml
                                                html={hosting.securityDetail}
                                                mode="rich"
                                                className="text-gray-600 dark:text-gray-300 prose dark:prose-invert max-w-none text-base leading-relaxed pl-14"
                                            />
                                        </div>
                                    </div>
                                )}

                                {hosting.resourcesDetail && (
                                    <div className={`space-y-10 ${!hosting.securityDetail && !hosting.backupsDetail ? 'lg:col-span-3' : (!hosting.securityDetail || !hosting.backupsDetail ? 'lg:col-span-1.5' : '')}`}>
                                        <div className="group">
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                                                <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                                                </div>
                                                Recursos
                                            </h3>
                                            <div
                                                className="text-gray-600 dark:text-gray-300 prose dark:prose-invert max-w-none text-base leading-relaxed pl-14"
                                            >
                                                {/* Check content length. If short (e.g. < 200 chars), show directly. If long, use accordion */}
                                                {hosting.resourcesDetail.length < 200 ? (
                                                    <SafeHtml html={hosting.resourcesDetail} mode="rich" />
                                                ) : (
                                                    <details className="group/details">
                                                        <summary className="list-none cursor-pointer flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
                                                            <span>Ver detalles completos</span>
                                                            <ChevronDown className="w-4 h-4 transition-transform group-open/details:rotate-180" />
                                                        </summary>
                                                        <SafeHtml
                                                            html={hosting.resourcesDetail}
                                                            mode="rich"
                                                            className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-800 animate-in fade-in slide-in-from-top-2 duration-200"
                                                        />
                                                    </details>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {hosting.backupsDetail && (
                                    <div className="space-y-10">
                                        <div className="group">
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                                                <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                                </div>
                                                Respaldos
                                            </h3>
                                            <SafeHtml
                                                html={hosting.backupsDetail}
                                                mode="rich"
                                                className="text-gray-600 dark:text-gray-300 prose dark:prose-invert max-w-none text-base leading-relaxed pl-14"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Bottom Row: Full Width */}
                                {hosting.dataCenterDetail && (
                                    <div className="col-span-1 md:col-span-2 lg:col-span-3 space-y-10 pt-8">
                                        <div className="group">
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                                                <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                </div>
                                                Centro de Datos
                                            </h3>
                                            <SafeHtml
                                                html={hosting.dataCenterDetail}
                                                mode="rich"
                                                className="pl-14 [&_ul]:flex [&_ul]:flex-wrap [&_ul]:gap-3 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:my-4 [&_li]:bg-gray-500 [&_li]:dark:bg-gray-700 [&_li]:text-white [&_li]:px-4 [&_li]:py-2 [&_li]:rounded-full [&_li]:text-sm [&_li]:font-medium"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">

                {/* Photo Gallery - Bento Grid Layout */}
                {hosting.photos && hosting.photos.length > 0 && (
                    <div className="mb-16">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                            Administración & Panel de Control
                        </h2>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-auto lg:h-[400px]">
                            {/* Main Featured Image (Left) */}
                            <div 
                                className="lg:col-span-2 h-[300px] lg:h-full rounded-2xl overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 group"
                                onClick={() => setSelectedImage(hosting.photos[0])}
                            >
                                <img
                                    src={hosting.photos[0]}
                                    alt="Panel Principal"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                            </div>

                            {/* Secondary Images (Right Column) */}
                            <div className="flex flex-col gap-6 h-full">
                                {hosting.photos.slice(1, 3).map((photo: string, idx: number) => (
                                    <div
                                        key={idx}
                                        className="flex-1 h-[200px] lg:h-auto rounded-2xl overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 group"
                                        onClick={() => setSelectedImage(photo)}
                                    >
                                        <img
                                            src={photo}
                                            alt={`Panel Vista ${idx + 1}`}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {relatedHostings.length > 0 && (
                <div className="bg-[#012654] dark:bg-slate-900 py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-2xl font-bold text-white mb-8">
                            Otras opciones
                        </h2>
                        <div className="relative group/carousel">
                            <div
                                ref={scrollContainerRef}
                                className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide scroll-smooth justify-center"
                            >
                                {relatedHostings.map((related: any) => (
                                    <div
                                        key={related.id}
                                        onClick={() => {
                                            navigate(`/hosting/${encodeURIComponent(related.name)}`);
                                            window.scrollTo(0, 0);
                                        }}
                                        className="min-w-[280px] w-[280px] bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer snap-start flex-shrink-0 border border-gray-100 dark:border-slate-700 p-6 flex flex-col items-center justify-center gap-4 group/card"
                                    >
                                        <div className="w-16 h-16 flex items-center justify-center bg-gray-50 dark:bg-slate-700 rounded-full group-hover/card:scale-110 transition-transform duration-300">
                                            {related.mainPhotoUrl ? (
                                                <img
                                                    src={related.mainPhotoUrl}
                                                    alt={related.displayName}
                                                    className="w-10 h-10 object-contain"
                                                />
                                            ) : (
                                                <span className="text-xl font-bold text-gray-400">
                                                    {related.displayName.substring(0, 2)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-center">
                                            <h3 className="font-bold text-gray-900 dark:text-white group-hover/card:text-blue-600 dark:group-hover/card:text-blue-400 transition-colors">
                                                {related.displayName}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                {related.payTypeDisplay || (related.payType === 'ANUAL' ? 'Anual' : 'Mensual')}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                        {/* Carousel Controls - Only show if content overflows */}
                        {(canScrollLeft || canScrollRight) && (
                            <>
                                <button
                                    onClick={() => scroll('left')}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-white dark:bg-slate-700 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-white hover:scale-110 transition-all opacity-0 group-hover/carousel:opacity-100 disabled:opacity-0"
                                    disabled={!canScrollLeft}
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <button
                                    onClick={() => scroll('right')}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-white dark:bg-slate-700 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-white hover:scale-110 transition-all opacity-0 group-hover/carousel:opacity-100 disabled:opacity-0"
                                    disabled={!canScrollRight}
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </>
                        )}
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
                        onClick={() => setSelectedImage(null)}
                    >
                        <X size={32} />
                    </button>

                    <button
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-50 p-2 bg-black/50 rounded-full"
                        onClick={handlePrevImage}
                    >
                        <ChevronLeft size={40} />
                    </button>

                    <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-50 p-2 bg-black/50 rounded-full"
                        onClick={handleNextImage}
                    >
                        <ChevronRight size={40} />
                    </button>

                    <img
                        src={selectedImage}
                        alt="Vista ampliada"
                        className="max-w-[70%] max-h-[80vh] object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

// Helper component for detail rows
const DetailRow = ({ label, value, highlight, icon }: { label: string; value: string; highlight?: boolean; icon?: React.ReactNode }) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
        <span className="text-gray-600 dark:text-gray-400 font-medium">{label}</span>
        <div className="flex items-center gap-2">
            <span className={`font-semibold ${highlight ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                {value}
            </span>
            {icon}
        </div>
    </div>
);

export default HostingDetail;
